import {Button, message} from 'antd'
import {
    course_getStudents_Api,
    studentLink_approveLinkRequest_Api,
    studentLink_getLinkRequests_Api,
    studentLink_rejectLinkRequest_Api
} from 'apis'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {StudentActionTypes} from 'views/AppLocals/Course'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {renderStudent} from '../functions/renderStudent'
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import {RiSearchEyeLine} from "react-icons/ri";
import {StudentFilter} from "../components/filters/StudentFilter";
import {useRouteSearchParam} from "../../../../envs/_current/history";
import {useStudentForm} from "../../../AppLocals/Course/CourseClass/useStudentForm";
import classNames from "classnames";
import {EntityPaginationMeta} from "../../../AppLocals/EntityList";
import {PiLinkSimpleBold, PiStudentFill} from "react-icons/pi";
import {formatSimplifyDate} from "../../../../helpers/dateUtils";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";

const RELATIONSHIP_LABELS = {
    SELF: 'self (student)',
    FATHER: 'father',
    MOTHER: 'mother',
    BROTHER: 'brother / sister',
    SISTER: 'sister',
    GUARDIAN: 'guardian',
}

const LinkRequestsPanel = ({owner_id, owner_type}) => {
    const t = useTranslate()
    const [requests, setRequests] = useState([])
    const [loaded, setLoaded] = useState(false)

    const {handleAsyncAction: loadRequests, isLoading} = useAsyncAction({
        apiInfo: studentLink_getLinkRequests_Api,
        onSuccess: (_, meta) => {
            setRequests(meta?.response?.data ?? [])
            setLoaded(true)
        },
    })

    const {handleAsyncAction: approveReq} = useAsyncAction({
        apiInfo: studentLink_approveLinkRequest_Api,
        onSuccess: () => {
            message.success(t('approved'))
            loadRequests({owner_id, owner_type, status: 'pending'})
        },
    })

    const {handleAsyncAction: rejectReq} = useAsyncAction({
        apiInfo: studentLink_rejectLinkRequest_Api,
        onSuccess: () => {
            message.info(t('rejected'))
            loadRequests({owner_id, owner_type, status: 'pending'})
        },
    })

    React.useEffect(() => {
        loadRequests({owner_id, owner_type, status: 'pending'})
    }, [owner_id, owner_type])

    if (!loaded && isLoading) {
        return <div className="flex justify-center py-10 text-color-400 text-sm">{t('loading')}...</div>
    }

    if (loaded && requests.length === 0) {
        return (
            <div className="flex flex-col items-center gap-3 py-12">
                <PiLinkSimpleBold size={36} className="text-color-300 opacity-50"/>
                <span className="text-sm text-color-400 italic">{t('no pending link requests')}</span>
            </div>
        )
    }

    return (
        <div className="flex flex-col divide-y divide-color-50 mt-2">
            {requests.map((req, i) => (
                <div key={i} className="py-3 px-4 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <PiStudentFill size={18} className="text-indigo-400"/>
                    </div>
                    <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                        <span className="font-semibold text-sm text-color-000">
                            {req?.student?.fullname || req?.student?.name || t('unknown student')}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-color-400 italic flex-wrap">
                            <span>{t('requested by')} :</span>
                            <span className="text-color-200 font-medium not-italic">
                                {req?.requested_by?.name || t('unknown')}
                            </span>
                            <span>·</span>
                            <span>{t(RELATIONSHIP_LABELS[req?.relationship] || req?.relationship)}</span>
                            {req?.created_at && (
                                <>
                                    <span>·</span>
                                    <span>{formatSimplifyDate(req.created_at)}</span>
                                </>
                            )}
                        </div>
                        {req?.notes && (
                            <span className="text-xs text-color-400 italic mt-0.5">{req.notes}</span>
                        )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                        <Button
                            size="small"
                            color="primary" variant="filled"
                            onClick={() => approveReq({user_id: req?.requested_by?.id}, {id: req?.id})}>
                            {t('approve')}
                        </Button>
                        <Button
                            size="small"
                            color="danger" variant="text"
                            onClick={() => rejectReq({}, {id: req?.id})}>
                            {t('reject')}
                        </Button>
                    </div>
                </div>
            ))}
        </div>
    )
}

const renderGridMeta = (props) =>
    (
        <LayoutContext.Consumer>
            {({isLg}) => (
                <div className={classNames(isLg ? 'col-span-2' : 'col-span-1')}>
                    <EntityPaginationMeta
                        {...props}
                        style={{position: 'inherit'}}
                    />
                </div>
            )}
        </LayoutContext.Consumer>
    )

const Students = ({
                      owner_id,
                      owner_type,
                      filter_actions = {
                          sortBy: 'sort-by',
                          keyword: 'keyword',
                          course_id: 'course',
                          course_class_id: 'course-class',
                          sub_class_name: 'sub-class-name',
                          include_archived: 'include-archived',
                      },
                  }) => {
    const t = useTranslate()
    const [activeTab, setActiveTab] = useState('students')

    const location = useLocation()

    const {isSm} = useContext(LayoutContext)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const course_id = useRouteSearchParam(filter_actions.course_id)
    const course_class_id = useRouteSearchParam(filter_actions.course_class_id)
    const sub_class_name = useRouteSearchParam(filter_actions.sub_class_name)
    const include_archived = useRouteSearchParam(filter_actions.include_archived)

    const {
        refreshKey,
        modal,
        handleAction,
        onCancel,
    } = useStudentForm({owner_id, owner_type})


    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view detail',
                        key: StudentActionTypes.STUDENT_DETAIL,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: StudentActionTypes.STUDENT_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: StudentActionTypes.STUDENT_DELETE,
                    },
                ].filter((e) => item),
                (key) => {
                    handleAction(key, item)
                }
            ),
        []
    )

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            course_id, course_class_id,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({
                course_id, course_class_id, sub_class_name, include_archived,
            })
            onSortChange(sortBy)
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: course_getStudents_Api,
            query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
            renderItem: renderStudent({
                translate: t,
                renderContextMenu,
                handleAction
            }),
            renderWrap: (child) => (
                <div className="mt-2 flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            )
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        dependencies: [owner_id, owner_type, course_id, course_class_id, sub_class_name, include_archived],
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: StudentFilter,
        isToggleDefault: true,
    })
    const tabs = [
        {id: 'students', label: 'students', icon: <PiStudentFill size={14}/>},
        {id: 'link_requests', label: 'link requests', icon: <PiLinkSimpleBold size={13}/>},
    ]

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('student management')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('manage the list of all students across the entire workspace including personal information learning history and status')}
                    </div>
                </div>
                {activeTab === 'students' && (
                    <Button
                        color="primary" variant="solid"
                        className="px-6 py-2 font-semibold rounded-lg"
                        onClick={() => {
                            handleAction(StudentActionTypes.STUDENT_CREATE)
                        }}>
                        {t('add new student')}
                    </Button>
                )}
            </div>

            {/* Tab switcher */}
            <div className="flex items-center gap-2">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={classNames(
                            "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                            activeTab === tab.id
                                ? 'bg-secondary-500 text-white'
                                : 'background hover:bg-secondary-50 shadow-sm hover:shadow-md text-color-200 hover:text-secondary-600'
                        )}>
                        {tab.icon}
                        {t(tab.label)}
                    </button>
                ))}
            </div>

            {activeTab === 'students' && pager_widget}
            {activeTab === 'link_requests' && (
                <LinkRequestsPanel
                    owner_id={owner_id}
                    owner_type={owner_type}
                />
            )}
            {modal}
            <div className="h-16"/>
        </div>
    )
}


export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(Students)
